import type { App, DirectiveBinding } from 'vue'
import Clipboard from 'clipboard'
import horizontalScroll from '/@/utils/horizontalScroll'
import { useEventListener } from '@vueuse/core'
import { isString } from 'lodash-es'
import { auth } from '/@/utils/common'
import Loading from '/@/components/Loading/index.vue'

export function directives (app: App) {
  // 鉴权指令
  authDirective(app)
  // 拖动指令
  dragDirective(app)
  // 缩放指令
  zoomDirective(app)
  // 点击后自动失焦指令
  blurDirective(app)
  // 自定义loading指令
  loadingDirective(app)
  // 表格横向拖动指令
  tableLateralDragDirective(app)
  // el-form-item去除for属性
  RemoveElFormItemFor(app)
  clipboardDirective(app)
  componentMountedDirective(app)
}

/**
 * 页面按钮鉴权指令
 * @description v-auth="'name'"，name可以为：index,add,edit,del,...
 */
function authDirective (app: App) {
  app.directive('auth', {
    mounted (el, binding) {
      if (!binding.value) return false
      if (!auth(binding.value)) el.parentNode.removeChild(el)
    },
  })
}

/**
 * 表格横向滚动指令
 * @description v-table-lateral-drag
 */
function tableLateralDragDirective (app: App) {
  app.directive('tableLateralDrag', {
    created (el) {
      new horizontalScroll(el.querySelector('.el-table__body-wrapper .el-scrollbar .el-scrollbar__wrap'))
    }
  })
}

/**
 * 点击后自动失焦指令
 * @description v-blur
 */
function blurDirective (app: App) {
  app.directive('blur', {
    mounted (el) {
      useEventListener(el, 'focus', () => el.blur())
    }
  })
}

/**
 * el-dialog 的缩放指令
 * 可以传递字符串和数组
 * 当为字符串时，传递dialog的class即可，实际被缩放的元素为'.el-dialog__body'
 * 当为数组时，参数一为句柄，参数二为实际被缩放的元素
 * @description v-zoom="'.handle-class-name'"
 * @description v-zoom="['.handle-class-name', '.zoom-dom-class-name', 句柄元素高度是否跟随缩放：默认false，句柄元素宽度是否跟随缩放：默认true]"
 */
function zoomDirective (app: App) {
  app.directive('zoom', {
    mounted (el, binding) {
      if (!binding.value) return false
      const zoomDomBindData = isString(binding.value) ? [ binding.value, '.el-dialog__body', false, true ] : binding.value
      zoomDomBindData[1] = zoomDomBindData[1] ? zoomDomBindData[1] : '.el-dialog__body'
      zoomDomBindData[2] = typeof zoomDomBindData[2] == 'undefined' ? false : zoomDomBindData[2]
      zoomDomBindData[3] = typeof zoomDomBindData[3] == 'undefined' ? true : zoomDomBindData[3]

      nextTick(() => {
        const zoomDom = document.querySelector(zoomDomBindData[1]) as HTMLElement // 实际被缩放的元素
        const zoomDomBox = document.querySelector(zoomDomBindData[0]) as HTMLElement // 动态添加缩放句柄的元素
        const zoomHandleEl = document.createElement('div') // 缩放句柄
        zoomHandleEl.className = 'zoom-handle'
        zoomHandleEl.onmouseenter = () => {
          zoomHandleEl.onmousedown = (e: MouseEvent) => {
            const x = e.clientX
            const y = e.clientY
            const zoomDomWidth = zoomDom.offsetWidth
            const zoomDomHeight = zoomDom.offsetHeight
            const zoomDomBoxWidth = zoomDomBox.offsetWidth
            const zoomDomBoxHeight = zoomDomBox.offsetHeight
            document.onmousemove = (e: MouseEvent) => {
              e.preventDefault() // 移动时禁用默认事件
              const w = zoomDomWidth + (e.clientX - x) * 2
              const h = zoomDomHeight + (e.clientY - y)

              zoomDom.style.width = `${w}px`
              zoomDom.style.height = `${h}px`

              if (zoomDomBindData[2]) {
                const boxH = zoomDomBoxHeight + (e.clientY - y)
                zoomDomBox.style.height = `${boxH}px`
              }
              if (zoomDomBindData[3]) {
                const boxW = zoomDomBoxWidth + (e.clientX - x) * 2
                zoomDomBox.style.width = `${boxW}px`
              }
            }

            document.onmouseup = function () {
              document.onmousemove = null
              document.onmouseup = null
            }
          }
        }

        zoomDomBox.appendChild(zoomHandleEl)
      })
    }
  })
}

const toggleLoading = (el, binding) => {
  // 插入到目标元素
  const insertDom = (parent, el) => {
    parent.appendChild(el.mask)
    el.vm.showLoading()
  }
  // 从目标元素中移除
  const removeDom = (parent, el) => {
    try {
      // parent.removeChild(el.mask)
      // el.mask.parentNode?.removeChild(el.mask)
    } catch (e) {
      console.warn(e)
    }
  }

  if (binding.value) {
    nextTick(() => {
      // 获取父元素的定位信息
      const isStatic = window.getComputedStyle(el).position === 'static'
      if (isStatic) {
        el.style.position = 'relative'
      }
      // 插入到目标元素
      insertDom(el, el)
    })
  } else {
    el.vm.closeLoading(() => removeDom(el, el))
  }
}

/**
 * v-sino-loading
 * @param app
 */
function loadingDirective (app: App) {
  app.directive('sino-loading', {
    mounted (el, binding) {
      const container = document.createElement('div')
      const instance = createApp(Loading)
      const vm = instance.mount(container)

      el.instance = instance
      el.vm = vm
      el.mask = vm.$el
      el.maskStyle = {}

      binding.value && toggleLoading(el, binding)
    },
    updated (el, binding) {
      if (binding.oldValue !== binding.value) toggleLoading(el, binding)
    },
    unmounted (el) {
      el.instance && el.instance.unmount()
    }
  })
}

/**
 * 拖动指令
 * @description v-drag="[domEl,handleEl]"
 * @description domEl=被拖动的元素，handleEl=在此元素内可以拖动`dom`
 */
interface downReturn {
  [key: string]: number
}
function dragDirective (app: App) {
  app.directive('drag', {
    mounted (el, binding) {
      if (!binding.value) return false

      const dragDom = document.querySelector(binding.value[0]) as HTMLElement
      const dragHandle = document.querySelector(binding.value[1]) as HTMLElement

      if (!dragHandle || !dragDom) {
        return false
      }

      dragHandle.onmouseover = () => (dragHandle.style.cursor = 'move')

      function down (e: MouseEvent | TouchEvent, type: string): downReturn {
        // 鼠标按下，计算当前元素距离可视区的距离
        const disX =
          type === 'pc'
            ? (e as MouseEvent).clientX - dragHandle.offsetLeft
            : (e as TouchEvent).touches[0].clientX - dragHandle.offsetLeft
        const disY =
          type === 'pc'
            ? (e as MouseEvent).clientY - dragHandle.offsetTop
            : (e as TouchEvent).touches[0].clientY - dragHandle.offsetTop

        // body宽度
        const screenWidth = document.body.clientWidth
        const screenHeight = document.body.clientHeight || document.documentElement.clientHeight

        // 被拖动元素宽度
        const dragDomWidth = dragDom.offsetWidth
        // 被拖动元素高度
        const dragDomheight = dragDom.offsetHeight

        // 拖动限位
        const minDragDomLeft = dragDom.offsetLeft
        const maxDragDomLeft = screenWidth - dragDom.offsetLeft - dragDomWidth
        const minDragDomTop = dragDom.offsetTop
        const maxDragDomTop = screenHeight - dragDom.offsetTop - dragDomheight

        // 获取到的值带px 正则匹配替换
        let styL: string | number = getComputedStyle(dragDom).left
        let styT: string | number = getComputedStyle(dragDom).top
        styL = +styL.replace(/px/g, '')
        styT = +styT.replace(/px/g, '')

        return {
          disX,
          disY,
          minDragDomLeft,
          maxDragDomLeft,
          minDragDomTop,
          maxDragDomTop,
          styL,
          styT
        }
      }

      function move (e: MouseEvent | TouchEvent, type: string, obj: downReturn) {
        const { disX, disY, minDragDomLeft, maxDragDomLeft, minDragDomTop, maxDragDomTop, styL, styT } = obj

        // 通过事件委托，计算移动的距离
        let left = type === 'pc' ? (e as MouseEvent).clientX - disX : (e as TouchEvent).touches[0].clientX - disX
        let top = type === 'pc' ? (e as MouseEvent).clientY - disY : (e as TouchEvent).touches[0].clientY - disY

        // 边界处理
        if (-left > minDragDomLeft) {
          left = -minDragDomLeft
        } else if (left > maxDragDomLeft) {
          left = maxDragDomLeft
        }

        if (-top > minDragDomTop) {
          top = -minDragDomTop
        } else if (top > maxDragDomTop) {
          top = maxDragDomTop
        }

        // 移动当前元素
        dragDom.style.cssText += `;left:${left + styL}px;top:${top + styT}px;`
      }

      dragHandle.onmousedown = e => {
        const obj = down(e, 'pc')
        document.onmousemove = e => {
          move(e, 'pc', obj)
        }
        document.onmouseup = () => {
          document.onmousemove = null
          document.onmouseup = null
        }
      }
      dragHandle.ontouchstart = e => {
        const obj = down(e, 'app')
        document.ontouchmove = e => {
          move(e, 'app', obj)
        }
        document.ontouchend = () => {
          document.ontouchmove = null
          document.ontouchend = null
        }
      }
    }
  })
}

const removeFor = (el: HTMLElement, binding: DirectiveBinding<any>) => {
  if (!el) {
    return
  }
  const isMultiple = binding.value === 'single' ? false : true
  const changeDomStyle = (dom: HTMLElement) => {
    if (!dom) {
      return
    }
    dom.removeAttribute('for')
    const cssTextArr: string[] = dom.style.cssText ? dom.style.cssText.split(';').filter(dom => dom) : [] as string[]
    dom.style.cssText = `${cssTextArr.join(';')};cursor: text;`
  }
  if (isMultiple){
    const labelDomArr = el.querySelectorAll('.el-form-item__label') as NodeListOf<HTMLElement>
    labelDomArr?.forEach(item => changeDomStyle(item))
  } else {
    const labelDom = el.querySelector('.el-form-item__label') as HTMLElement
    changeDomStyle(labelDom)
  }
}

/**
 * v-remove-el-form-item-for
 * @param app
 * @description 使用方法：
 * 一：<el-form v-remove-el-form-item-for></el-form>
      不传参数时，移除指令下所有el-form-item的for属性
 * 二：<el-form v-remove-el-form-item-for="'single'"></el-form>
      指定single属性时，值移除当前指令下的第一个el-form-item的for属性
 */
function RemoveElFormItemFor (app: App) {
  app.directive('remove-el-form-item-for', {
    mounted (el, binding) {
      nextTick(() => removeFor(el, binding))
    },
    updated (el, binding) {
      removeFor(el, binding)
    },
    unmounted () {}
  })
}

/**
 * v-component-mounted
 * 组件mounted回调指令，用于在组件mounted时执行回调函数
 * @param app
 */
function clipboardDirective (app: App){
  app.directive('clipboard', {
    mounted (el, binding) {
      switch (binding.arg) {
        case 'success':
          el._vClipBoard_success = binding.value
          break
        case 'error':
          el._vClipBoard_error = binding.value
          break
        default: {
          const clipboard = new Clipboard(el, {
            text: () => binding.value,
            action: () => binding.arg === 'cut' ? 'cut' : 'copy'
          })
          clipboard.on('success', e => {
            const callback = el._vClipBoard_success
            callback && callback(e)
          })
          clipboard.on('error', e => {
            const callback = el._vClipBoard_error
            callback && callback(e)
          })
          el._vClipBoard = clipboard
        }
      }
    },
    updated (el, binding) {
      if (binding.arg === 'success') {
        el._vClipBoard_success = binding.value
      } else if (binding.arg === 'error') {
        el._vClipBoard_error = binding.value
      } else {
        el._vClipBoard.text = function () { return binding.value }
        el._vClipBoard.action = () => binding.arg === 'cut' ? 'cut' : 'copy'
      }
    },
    unmounted (el, binding) {
      if (!el._vClipboard) return
      if (binding.arg === 'success') {
        delete el._vClipBoard_success
      } else if (binding.arg === 'error') {
        delete el._vClipBoard_error
      } else {
        el._vClipBoard.destroy()
        delete el._vClipBoard
      }
    }
  })
}

function componentMountedDirective (app: App) {
  app.directive('component-mounted', {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    mounted (el, binding) {
      binding.value && binding.value(el)
    }
  })
}
